import React from 'react';
import { motion } from 'framer-motion';
import { Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const GoalsProgress = ({ metrics }) => {
    return (
        <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                    <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                        <Target className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                    </div>
                    <span className="text-foreground/90 font-medium">Monthly Goals Progress</span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {Object.entries(metrics).map(([key, metric]) => {
                        const isPositive = metric.change >= 0;
                        const progressPercentage = Math.min(Math.round((metric.current / metric.goal) * 100), 100);

                        return (
                            <motion.div
                                key={key}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-2"
                            >
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium capitalize text-sm text-foreground/95 dark:text-foreground/90">{key}</span>
                                        <div className={`px-2.5 py-1 rounded-lg text-xs transition-colors ${isPositive
                                            ? 'text-[hsl(142.1_76.2%_36.3%)] dark:text-[hsl(142.1_76.2%_45.3%)] bg-[hsl(142.1_76.2%_36.3%)]/5 group-hover:bg-[hsl(142.1_76.2%_36.3%)]/10'
                                            : progressPercentage < 30
                                                ? 'text-[hsl(0_84.2%_60.2%)] dark:text-[hsl(0_84.2%_70.2%)] bg-[hsl(0_84.2%_60.2%)]/5 group-hover:bg-[hsl(0_84.2%_60.2%)]/10'
                                                : 'text-[hsl(215_20%_65%)] dark:text-[hsl(215_20%_75%)] bg-[hsl(215_20%_65%)]/5 group-hover:bg-[hsl(215_20%_65%)]/10'
                                            }`}>
                                            <span className={`inline-flex items-center gap-1 ${isPositive
                                                ? 'text-[hsl(142.1_76.2%_36.3%)] dark:text-[hsl(142.1_76.2%_45.3%)]'
                                                : progressPercentage < 30
                                                    ? 'text-[hsl(0_84.2%_60.2%)] dark:text-[hsl(0_84.2%_70.2%)]'
                                                    : 'text-[hsl(215_20%_65%)] dark:text-[hsl(215_20%_75%)]'
                                                }`}>
                                                {isPositive ? (
                                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 5l7 7-7 7M5 12h14" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 19l-7-7 7-7M19 12H5" />
                                                    </svg>
                                                )}
                                                {Math.abs(progressPercentage)}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`text-sm font-medium ${isPositive
                                        ? 'text-[hsl(142.1_76.2%_36.3%)] dark:text-[hsl(142.1_76.2%_45.3%)]'
                                        : 'text-[hsl(0_84.2%_60.2%)] dark:text-[hsl(0_84.2%_70.2%)]'
                                        }`}>
                                        {metric.current.toLocaleString()}
                                    </div>
                                </div>
                                <div className="h-1.5 rounded-full bg-primary/10 overflow-hidden">
                                    <motion.div
                                        className={`h-full ${isPositive
                                            ? 'bg-[hsl(142.1_76.2%_36.3%)] dark:bg-[hsl(142.1_76.2%_45.3%)]'
                                            : progressPercentage < 30
                                                ? 'bg-[hsl(0_84.2%_60.2%)] dark:bg-[hsl(0_84.2%_70.2%)]'
                                                : 'bg-[hsl(215_20%_65%)] dark:bg-[hsl(215_20%_75%)]'
                                            }`}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progressPercentage}%` }}
                                        transition={{ duration: 0.5, ease: "easeOut" }}
                                    />
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground/60">
                                    <span>Target: {metric.goal.toLocaleString()}</span>
                                    <span className="text-muted-foreground/80">
                                        {isPositive ? '+' : '-'}{Math.round(Math.abs(metric.change))}% vs prev
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

export default GoalsProgress; 